import { MultiSelect } from "@design-system/shadcn";
import React, { useCallback, useMemo } from "react";

import { useFieldContext } from "../core/form-contexts";

type FormFieldMultiSelectProps = Omit<
  React.ComponentProps<typeof MultiSelect>,
  "name" | "value" | "onValueChange"
> & {
  name?: string;
  value?: string[];
  onValueChange?: (value: string[]) => void;
};

function FormFieldMultiSelect({
  name,
  value,
  onValueChange,
  children,
  ...props
}: FormFieldMultiSelectProps) {
  const field = useFieldContext<string[]>();

  if (!field) {
    throw new Error("FormFieldMultiSelect must be used within form.AppField");
  }

  const hasError = useMemo(
    () => field.state.meta.errors.length > 0,
    [field.state.meta.errors.length]
  );
  const changeHandler = useCallback(
    (val: string[]) => field.handleChange(val),
    [field]
  );

  return (
    <MultiSelect
      name={name ?? field.name}
      value={value ?? field.state.value ?? []}
      onValueChange={onValueChange ?? changeHandler}
      aria-invalid={hasError}
      {...props}
    >
      {children}
    </MultiSelect>
  );
}

export type { MultiSelectOption } from "@design-system/shadcn";

export { FormFieldMultiSelect };
export type { FormFieldMultiSelectProps };
